import { useState } from "react";
import { Card, Tag, Typography, Space, Divider, Tooltip } from "antd";
import { useHistory } from "react-router-dom";
import {
  UserOutlined,
  TruckOutlined,
  FilePdfOutlined,
  CarOutlined,
  NumberOutlined,
  FileAddOutlined,
  ShopOutlined,
  SyncOutlined,
  MailOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  BankOutlined,
  BoxPlotOutlined,
} from "@ant-design/icons";
import { dineroDisabledMask, ButtonItem } from "./Items";

const { Text } = Typography;

export const ORIGEN_CONFIG = {
  bodega_local: { color: "#13c2c2", antd: "cyan", label: "LOCAL" },
  dropshipping_urrea: { color: "#73d13d", antd: "green", label: "URREA" },
  envio_manual: { color: "#fa8c16", antd: "orange", label: "MANUAL" },
  entrega_sucursal: { color: "#eb2f96", antd: "magenta", label: "SUCURSAL" },
};

const VentaOnlineCard = ({
  venta,
  envio,
  onVerGuia,
  onVerificarPago,
  onVerificarPagoMattilda,
  onPagarManualmente,
  onConfirmarTransferencia,
  onReenviarCorreo,
  onVerEstadoGeneral,
  onSeguirEmpaquetando,
  badgeGrupo,
  mostrarDetalles = false,
  permisoConfirmarPago = false,
}) => {
  const history = useHistory();
  const [verificandoPago, setVerificandoPago] = useState(false);
  const [verificandoPagoMattilda, setVerificandoPagoMattilda] = useState(false);
  const [pagandoManual, setPagandoManual] = useState(false);
  const [confirmandoTransferencia, setConfirmandoTransferencia] =
    useState(false);
  const [reenviando, setReenviando] = useState(false);
  const verDetalle = mostrarDetalles;
  const { clienteOnline, nombreCliente, serieNumero, _id } = venta;
  const isLocal = envio.origen === "bodega_local";
  const isPickup = envio.origen === "entrega_sucursal";
  const mostrarBotonRequisicion =
    isLocal && !!venta.requiereRequisicion && !venta.requisicionId;
  const estaCancelada = venta.estado === 0;
  const mostrarBotonVerificarPago =
    !!onVerificarPago &&
    venta.estadoPago !== 3 &&
    !!venta.idPagoConekta &&
    !estaCancelada;
  const mostrarBotonVerificarPagoMattilda =
    !!onVerificarPagoMattilda &&
    venta.estadoPago !== 3 &&
    !!venta.idPagoMattilda &&
    !estaCancelada;
  const mostrarBotonPagarManual =
    !!onPagarManualmente &&
    !!permisoConfirmarPago &&
    venta.estadoPago !== 3 &&
    !estaCancelada;
  const mostrarBotonConfirmarTransferencia =
    !!onConfirmarTransferencia &&
    !!permisoConfirmarPago &&
    venta.pasarelaPagoOnline === "transferenciaFija" &&
    venta.estadoPago !== 3 &&
    !estaCancelada;
  const mostrarBotonReenviarCorreo =
    !!onReenviarCorreo && !!clienteOnline?.email;

  const handleVerificarPago = async (e) => {
    e.stopPropagation();
    if (verificandoPago) return;
    setVerificandoPago(true);
    try {
      await onVerificarPago(venta._id);
    } finally {
      setVerificandoPago(false);
    }
  };

  const handleVerificarPagoMattilda = async (e) => {
    e.stopPropagation();
    if (verificandoPagoMattilda) return;
    setVerificandoPagoMattilda(true);
    try {
      await onVerificarPagoMattilda(venta._id);
    } finally {
      setVerificandoPagoMattilda(false);
    }
  };

  const handlePagarManual = async (e) => {
    e.stopPropagation();
    if (pagandoManual) return;
    setPagandoManual(true);
    try {
      await onPagarManualmente(venta._id);
    } finally {
      setPagandoManual(false);
    }
  };

  const handleConfirmarTransferencia = async (e) => {
    e.stopPropagation();
    if (confirmandoTransferencia) return;
    setConfirmandoTransferencia(true);
    try {
      await onConfirmarTransferencia(venta._id);
    } finally {
      setConfirmandoTransferencia(false);
    }
  };

  const handleReenviarCorreo = async (e) => {
    e.stopPropagation();
    if (reenviando) return;
    setReenviando(true);
    try {
      await onReenviarCorreo();
    } finally {
      setReenviando(false);
    }
  };

  const irAHacerRequisicion = (e) => {
    e.stopPropagation();
    history.push("/panel/requerirInsumosVenta", {
      ventaId: _id,
      prefillFaltanteInventario: true,
    });
  };

  const origenCfg = ORIGEN_CONFIG[envio.origen] || ORIGEN_CONFIG.bodega_local;
  const accentColor = origenCfg.color;

  const irAVerVenta = (e) => {
    e.stopPropagation();
    history.push("/panel/verVenta", { id: _id });
  };

  return (
    <Card
      hoverable
      size="small"
      onDoubleClick={irAVerVenta}
      onContextMenu={(e) => {
        e.preventDefault();
        if (onVerEstadoGeneral) onVerEstadoGeneral();
      }}
      style={{
        borderRadius: "8px",
        marginBottom: "4px",
        borderLeft: `5px solid ${accentColor}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        backgroundColor: "#fff",
        width: "100%",
      }}
      bodyStyle={{ padding: "8px 10px" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 5,
        }}
      >
        <Text strong style={{ fontSize: "12px" }}>
          {serieNumero || "S/F"}
        </Text>
        <Space size={4}>
          {estaCancelada && (
            <Tag color="red" style={{ fontSize: "9px", margin: 0 }}>
              CANCELADA
            </Tag>
          )}
          <Tag color={origenCfg.antd} style={{ fontSize: "9px", margin: 0 }}>
            {origenCfg.label}
          </Tag>
        </Space>
      </div>
      {badgeGrupo && (
        <Tag color="blue" style={{ fontSize: 9, marginBottom: 4, display: "block" }}>
          {badgeGrupo}
        </Tag>
      )}
      {venta.escuelaNombre && (
        <Tag
          color="purple"
          style={{
            fontSize: "9px",
            marginBottom: 4,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {venta.escuelaNombre}
        </Tag>
      )}

      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        {(() => {
          const partes = [clienteOnline?.nombre, clienteOnline?.apellidoPaterno]
            .map((p) => (p ? String(p).trim() : ""))
            .filter(Boolean);
          const nombreMostrar =
            partes.join(" ") || clienteOnline?.email || "Cliente sin nombre";
          return (
            <Text
              style={{ fontSize: "12px" }}
              ellipsis={{ tooltip: nombreMostrar }}
            >
              <UserOutlined style={{ marginRight: 5, color: "#8c8c8c" }} />
              {nombreMostrar}
            </Text>
          );
        })()}

        {nombreCliente && (
          <Text
            style={{ fontSize: "11px", color: "#bfbfbf" }}
            ellipsis={{ tooltip: nombreCliente }}
          >
            <UserOutlined style={{ marginRight: 5, color: "#bfbfbf" }} />
            {nombreCliente}
          </Text>
        )}

        {verDetalle && venta.paqueteEscolarNombre && (
          <Text
            style={{ fontSize: "11px", color: "#722ed1" }}
            ellipsis={{ tooltip: venta.paqueteEscolarNombre }}
          >
            {venta.paqueteEscolarNombre}
          </Text>
        )}

        {verDetalle &&
          (isPickup ? (
            <Text
              type="secondary"
              style={{ fontSize: "10px" }}
              ellipsis={{
                tooltip: envio.pickupBranch?.label || "Recoger en sucursal",
              }}
            >
              <ShopOutlined style={{ marginRight: 5 }} />
              {envio.pickupBranch?.label || "Recoger en sucursal"}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: "10px" }}>
              <TruckOutlined style={{ marginRight: 5 }} />
              {envio.carrier?.toUpperCase()} - {envio.service}
            </Text>
          ))}
      </Space>
      {verDetalle && envio.trackingNumber && (
        <div style={{ marginTop: "6px" }}>
          <Text style={{ fontSize: "10px", color: "#1890ff" }} strong>
            <NumberOutlined style={{ marginRight: "4px" }} />
            Rastreo: {envio.trackingNumber}
          </Text>
        </div>
      )}
      {verDetalle && envio.folioRecoleccion && (
        <Text style={{ fontSize: "10px", color: "#8c8c8c" }} strong>
          <CarOutlined style={{ marginRight: "4px" }} />
          N° Recolección:
          <Tag color="cyan" style={{ fontSize: "10px", margin: 0 }}>
            {envio.folioRecoleccion}
          </Tag>
        </Text>
      )}

      {verDetalle && <Divider style={{ margin: "6px 0" }} />}

      {verDetalle &&
        (isPickup ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong style={{ fontSize: "10px" }}>
              Envío:
            </Text>
            <Text strong style={{ color: "#eb2f96", fontSize: "11px" }}>
              Entrega Presencial
            </Text>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <Tooltip title="Costo del envío de este paquete: lo que cotiza la paquetería y lo que se le cobra al cliente por el envío. No es el total de la venta.">
              <Text
                strong
                style={{
                  fontSize: "9px",
                  color: "#8c8c8c",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Costo del envío{" "}
                <InfoCircleOutlined style={{ fontSize: "9px" }} />
              </Text>
            </Tooltip>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text type="secondary" style={{ fontSize: "9px" }}>
                Cotización Inicial:
              </Text>
              <Text
                style={{
                  fontSize: "9px",
                  color: "#8c8c8c",
                  textDecoration: envio.costoEtiqueta ? "line-through" : "none",
                }}
              >
                {dineroDisabledMask(envio.costoApi)}
              </Text>
            </div>

            {envio.costoEtiqueta > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong style={{ fontSize: "9px" }}>
                  Costo Real (Facturado):
                </Text>
                <Text
                  strong
                  style={{
                    fontSize: "10px",
                    color:
                      envio.costoEtiqueta > envio.costoCliente
                        ? "#cf1322"
                        : "#fa8c16",
                  }}
                >
                  {dineroDisabledMask(envio.costoEtiqueta)}
                </Text>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text strong style={{ fontSize: "10px" }}>
                Cobro al Cliente:
              </Text>
              <Text strong style={{ color: "#46a815", fontSize: "12px" }}>
                {dineroDisabledMask(envio.costoCliente)}
              </Text>
            </div>
          </div>
        ))}

      {verDetalle &&
        (() => {
          const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
          const totalVenta = num(venta.totalVenta);
          if (totalVenta === null) return null;
          const totalProductos = num(venta.totalProductos) ?? 0;
          const totalEnvio = num(venta.totalEnvio) ?? 0;
          return (
            <>
              <Divider style={{ margin: "6px 0" }} />
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <Tooltip title="Costo total de la venta. El envío real ya está incluido en el Total; aquí se desglosa cuánto corresponde a productos y cuánto al envío.">
                  <Text
                    strong
                    style={{
                      fontSize: "9px",
                      color: "#8c8c8c",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Total de la venta{" "}
                    <InfoCircleOutlined style={{ fontSize: "9px" }} />
                  </Text>
                </Tooltip>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: "9px" }}>
                    Productos:
                  </Text>
                  <Text style={{ fontSize: "9px", color: "#8c8c8c" }}>
                    {dineroDisabledMask(totalProductos)}
                  </Text>
                </div>

                {totalEnvio > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: "9px" }}>
                      Envío:
                    </Text>
                    <Text style={{ fontSize: "9px", color: "#8c8c8c" }}>
                      {dineroDisabledMask(totalEnvio)}
                    </Text>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text strong style={{ fontSize: "10px" }}>
                    Total:
                  </Text>
                  <Text strong style={{ color: accentColor, fontSize: "12px" }}>
                    {dineroDisabledMask(totalVenta)}
                  </Text>
                </div>
              </div>
            </>
          );
        })()}
      {(() => {
        const lineasLibroVirtual = (venta.insumos || []).filter(
          (ins) => ins.libroVirtual,
        );
        if (lineasLibroVirtual.length === 0) return null;
        const codigos = lineasLibroVirtual.flatMap(
          (ins) => ins.codigosLibroVirtual || [],
        );
        const lineasConError = lineasLibroVirtual.filter(
          (ins) => ins.libroVirtualError,
        );
        return (
          <div style={{ marginTop: 4, marginBottom: 4 }}>
            {codigos.length > 0 && (
                <Tag
                  color="purple"
                  style={{ fontSize: "9px", marginBottom: 4, display: "block" }}
                >
                  Código Virtual {codigos.map((c) => c.numero).join(", ")}
                </Tag>
            )}
            {lineasConError.length > 0 && (
              <Tag
                color="red"
                style={{ fontSize: "9px", marginBottom: 4, display: "block" }}
              >
                Código de Virtual pendiente de asignar
              </Tag>
            )}
          </div>
        );
      })()}
      {(() => {
        const hayGuia = envio.guiaGenerada && envio.labelUrl;
        const hayAcciones =
          hayGuia ||
          mostrarBotonVerificarPago ||
          mostrarBotonVerificarPagoMattilda ||
          mostrarBotonPagarManual ||
          mostrarBotonConfirmarTransferencia ||
          mostrarBotonRequisicion ||
          mostrarBotonReenviarCorreo ||
          !!onSeguirEmpaquetando;
        if (!hayAcciones) return null;
        return (
          <>
            <Divider style={{ margin: "6px 0" }} />
            {mostrarBotonRequisicion && (
              <Tag
                color="orange"
                style={{ fontSize: "9px", marginBottom: 6, display: "block" }}
              >
                Faltan insumos en inventario
              </Tag>
            )}
            <div
              style={{
                display: "flex",
                gap: 6,
                justifyContent: "center",
              }}
            >
              {hayGuia && (
                <Tooltip title="Ver Guía">
                  <ButtonItem
                    type="primary"
                    size="small"
                    icon={<FilePdfOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onVerGuia) onVerGuia();
                    }}
                    style={{ height: 24, padding: "0 10px" }}
                    lima
                  />
                </Tooltip>
              )}
              {mostrarBotonVerificarPago && (
                <Tooltip
                  title={verificandoPago ? "Consultando..." : "Verificar Pago"}
                >
                  <ButtonItem
                    type="primary"
                    size="small"
                    icon={<SyncOutlined spin={verificandoPago} />}
                    onClick={handleVerificarPago}
                    disabled={verificandoPago}
                    style={{ height: 24, padding: "0 10px" }}
                    azul
                  />
                </Tooltip>
              )}
              {mostrarBotonVerificarPagoMattilda && (
                <Tooltip
                  title={
                    verificandoPagoMattilda
                      ? "Consultando..."
                      : "Verificar Pago (Matilda)"
                  }
                >
                  <ButtonItem
                    type="primary"
                    size="small"
                    icon={<SyncOutlined spin={verificandoPagoMattilda} />}
                    onClick={handleVerificarPagoMattilda}
                    disabled={verificandoPagoMattilda}
                    style={{ height: 24, padding: "0 10px" }}
                    lila
                  />
                </Tooltip>
              )}
              {mostrarBotonConfirmarTransferencia && (
                <Tooltip
                  title={
                    confirmandoTransferencia
                      ? "Procesando..."
                      : "Confirmar transferencia"
                  }
                >
                  <ButtonItem
                    type="primary"
                    size="small"
                    icon={
                      confirmandoTransferencia ? (
                        <SyncOutlined spin />
                      ) : (
                        <BankOutlined />
                      )
                    }
                    onClick={handleConfirmarTransferencia}
                    disabled={confirmandoTransferencia}
                    style={{ height: 24, padding: "0 10px" }}
                    cyan
                  />
                </Tooltip>
              )}
              {mostrarBotonPagarManual && (
                <Tooltip
                  title={pagandoManual ? "Procesando..." : "Pagar manualmente"}
                >
                  <ButtonItem
                    type="primary"
                    size="small"
                    icon={
                      pagandoManual ? <SyncOutlined spin /> : <DollarOutlined />
                    }
                    onClick={handlePagarManual}
                    disabled={pagandoManual}
                    style={{ height: 24, padding: "0 10px" }}
                    verde
                  />
                </Tooltip>
              )}
              
              {mostrarBotonRequisicion && (
                <Tooltip title="Hacer Requisición">
                  <ButtonItem
                    type="primary"
                    size="small"
                    icon={<FileAddOutlined />}
                    onClick={irAHacerRequisicion}
                    style={{ height: 24, padding: "0 10px" }}
                    naranja
                  />
                </Tooltip>
              )}
              {mostrarBotonReenviarCorreo && (
                <Tooltip title={reenviando ? "Enviando..." : "Reenviar correo"}>
                  <ButtonItem
                    size="small"
                    icon={reenviando ? <SyncOutlined spin /> : <MailOutlined />}
                    onClick={handleReenviarCorreo}
                    disabled={reenviando}
                    style={{ height: 24, padding: "0 10px" }}
                    blanco
                  />
                </Tooltip>
              )}
              {onSeguirEmpaquetando && (
                <Tooltip title="Seguir empaquetando (faltan insumos por marcar)">
                  <ButtonItem
                    type="primary"
                    size="small"
                    icon={<BoxPlotOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSeguirEmpaquetando();
                    }}
                    style={{ height: 24, padding: "0 10px" }}
                    naranja
                  />
                </Tooltip>
              )}
            </div>
          </>
        );
      })()}
    </Card>
  );
};

export default VentaOnlineCard;
